import { LogOut, Repeat } from 'lucide-react';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { useI18nContext } from '@/i18n/i18n-react';
import * as api from '@/lib/api-client';
import { cn } from '@/lib/utils';

export interface OnboardingAccountActionsProps {
  /** Icons only (labels kept for screen readers), for the mobile Studio canvas. */
  compact?: boolean;
  className?: string;
}

// Escape hatches for the onboarding flow, which has no sidebar: switch to
// another app/tenant (the select-tenant page) or log out.
export function OnboardingAccountActions({
  compact = false,
  className,
}: OnboardingAccountActionsProps) {
  const navigate = useNavigate();
  const { LL } = useI18nContext();

  const switchTenant = () => {
    // select-tenant auto-redirects back to onboarding when the user has a
    // single creation request and no tenants; this flag opts out so the user
    // actually lands on the list.
    navigate('/select-tenant', { state: { skipAutoRedirect: true } });
  };

  const logout = async () => {
    await api.logout();
    navigate('/login');
  };

  const buttonClassName = compact
    ? 'size-9 text-foreground/70'
    : 'gap-2 text-muted-foreground';
  const label = (text: string) =>
    compact ? <span className="sr-only">{text}</span> : text;

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <Button
        variant="ghost"
        size={compact ? 'icon' : 'sm'}
        className={buttonClassName}
        onClick={switchTenant}
      >
        <Repeat className="h-4 w-4" />
        {label(LL.tenantSwitcher.switchTenant())}
      </Button>
      <Button
        variant="ghost"
        size={compact ? 'icon' : 'sm'}
        className={buttonClassName}
        onClick={logout}
      >
        <LogOut className="h-4 w-4" />
        {label(LL.logout())}
      </Button>
    </div>
  );
}
